/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Wed Apr 03 2024
 **/

import styled from "@emotion/styled";

import { Button, Column, Divider, Row, Tooltip, Typography, TypographyCSS } from "@/frameworks/designStuff/components";
import { _T, _P } from "@rfice/language";
import { Maybe } from "@/frameworks/libraries/components";
import { useProperties } from "@/frameworks/libraries/properties";
import { colors } from "@/frameworks/core/styles";
import { useGroup, useSpace } from "@/frameworks/core/API";

import * as S from "../private/emotion";
import { useQueryClient } from "@tanstack/react-query";
import { GroupActions, SpaceActions } from "@/frameworks/core/persistence";
import { GroupCacheOption } from "@/frameworks/core/persistence/groups/actions";

export default function GroupNoticePopup({
	groupInfo,
	onClose
}: {
	groupInfo: PushServices.Group.GroupInvited.Data;
	onClose: () => void;
}) {
	const properties = useProperties();
	const spaceAPI = useSpace();
	const queryClient = useQueryClient();
	const groupAPI = useGroup({
		onSuccess(name) {
			switch (name) {
				case "invite:process": {
					onClose();
					break;
				}
			}
		}
	});

	const handleJoinGroup = async () => {
		await groupAPI.invite.process.execute({
			groupId: groupInfo.groupInfo.id,
			approve: true
		});
		// Update 21/02/2025: Update list group to  group persisted when the user accepts invited
		await GroupActions.instance.refreshGroupCache(GroupCacheOption.LIST, () => {
			// Invalidate query getGroupSpaceChatList
			queryClient.invalidateQueries({ queryKey: ["getGroupSpaceChatList"] });

			// Invalidate query space & mySpaces
			queryClient.invalidateQueries({ queryKey: ["space", "mySpaces"] });
			SpaceActions.instance.initialize();
		});
	};

	const handleRejectGroup = () => {
		return groupAPI.invite.process.execute({
			groupId: groupInfo.groupInfo.id,
			approve: false
		});
	};

	const handleDeclineGroup = () => {
		handleRejectGroup()
			.then(() => {
				handleRejectGroup();
			})
			.finally(() => {
				spaceAPI.mySpaces.list.execute();
			});
	};
	return (
		<S.NoticeWrapper gap={12}>
			<Row css={{ position: "absolute", right: "20px", top: "8px" }}>
				<Button.Icon name="Close24" filled="transparent" variant="contained" size="medium" onClick={onClose} />
			</Row>
			<Row gap={16}>
				<S.ImageBox bgcolor={colors[`${groupInfo.groupInfo.colorLogo}_50_100` as keyof Colors]}>
					<Maybe
						test={Boolean(groupInfo.groupInfo.logoPath)}
						fallback={<S.NameTypo typoSize="Head6">{groupInfo.groupInfo.name}</S.NameTypo>}
					>
						<S.Img src={properties.default.webDomain + groupInfo.groupInfo.logoPath} alt="img" />
					</Maybe>
				</S.ImageBox>
				<Column gap={8}>
					<Typography typoSize="Head6">{_T("그룹 초대")}</Typography>
					<Column>
						<UserName typoSize="Body3_MD">
							{_P("{{groupName}} \n{{name}} 님이 그룹 멤버로 초대합니다.", {
								name: `<span id='target'>${groupInfo.sender.name}</span>`,
								groupName: groupInfo.groupInfo.name
							})}
						</UserName>
						{groupInfo?.inviteMessage && (
							<Tooltip
								message={groupInfo?.inviteMessage}
								style={{ maxWidth: "400px", wordBreak: "break-all" }}
								position="bottom"
								hasIcon={false}
								isShow={false}
							>
								<Typography
									style={{
										display: "-webkit-box",
										WebkitLineClamp: 2,
										WebkitBoxOrient: "vertical",
										overflow: "hidden",
										textOverflow: "ellipsis",
										width: "fit-content",
										maxWidth: 400
									}}
								>
									{groupInfo?.inviteMessage}
								</Typography>
							</Tooltip>
						)}
					</Column>
				</Column>
			</Row>
			<Divider type="line" />
			<Row style={{ width: "100%" }} horizonAlign="right" gap={8}>
				<Button filled="primary" onClick={handleDeclineGroup}>
					{_T("거절")}
				</Button>
				<Button filled="primary" onClick={handleJoinGroup}>
					{_T("수락")}
				</Button>
			</Row>
		</S.NoticeWrapper>
	);
}

const UserName = styled(Typography)`
	#target {
		${TypographyCSS("Title3")}
	}
	white-space: pre-wrap;
`;
